import { useEffect, useImperativeHandle, useRef, forwardRef, useState } from "react";

/**
 * Touch + mouse + stylus friendly signature pad.
 * Exposes via ref: { toDataURL(), clear(), isEmpty() }
 */
const SignaturePad = forwardRef(function SignaturePad({ className = "", height = 220 }, ref) {
  const canvasRef = useRef(null);
  const drawingRef = useRef(false);
  const lastRef = useRef({ x: 0, y: 0 });
  const dirtyRef = useRef(false);
  const [, setTick] = useState(0); // re-render to toggle "clear" enabled state

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const resize = () => {
      const dpr = window.devicePixelRatio || 1;
      const rect = canvas.getBoundingClientRect();
      const newW = Math.round(rect.width * dpr);
      const newH = Math.round(rect.height * dpr);
      // No-op if size hasn't actually changed. This is critical on mobile:
      // scrolling can trigger resize events (browser toolbar show/hide) that
      // would otherwise WIPE the customer's signature. We only re-init when
      // the canvas is genuinely being resized.
      if (canvas.width === newW && canvas.height === newH) return;

      // Preserve the current drawing before re-initialising the canvas.
      // Without this, opening the virtual keyboard / rotating the device /
      // any scroll-driven viewport change erased the customer's signature —
      // a real-world complaint from Rent Lupi customers at the desk.
      let saved = null;
      if (dirtyRef.current && canvas.width > 0 && canvas.height > 0) {
        saved = document.createElement("canvas");
        saved.width = canvas.width;
        saved.height = canvas.height;
        saved.getContext("2d").drawImage(canvas, 0, 0);
      }

      canvas.width = newW;
      canvas.height = newH;
      const ctx = canvas.getContext("2d");
      ctx.scale(dpr, dpr);
      ctx.lineCap = "round";
      ctx.lineJoin = "round";
      ctx.strokeStyle = "#0a0a0a";
      ctx.lineWidth = 2.4;
      // Fill white background so signature has proper PNG export
      ctx.fillStyle = "#ffffff";
      ctx.fillRect(0, 0, rect.width, rect.height);

      if (saved) {
        // Draw the saved signature back onto the resized canvas, scaled to
        // fit the new CSS size. Slight loss of quality is acceptable —
        // losing the signature entirely is not.
        ctx.drawImage(saved, 0, 0, rect.width, rect.height);
      }
    };
    resize();
    window.addEventListener("resize", resize);
    return () => window.removeEventListener("resize", resize);
  }, []);

  const getPoint = (e) => {
    const canvas = canvasRef.current;
    const rect = canvas.getBoundingClientRect();
    if (e.touches && e.touches.length) {
      return { x: e.touches[0].clientX - rect.left, y: e.touches[0].clientY - rect.top };
    }
    return { x: e.clientX - rect.left, y: e.clientY - rect.top };
  };

  const start = (e) => {
    e.preventDefault();
    drawingRef.current = true;
    lastRef.current = getPoint(e);
  };

  const move = (e) => {
    if (!drawingRef.current) return;
    e.preventDefault();
    const p = getPoint(e);
    const ctx = canvasRef.current.getContext("2d");
    ctx.beginPath();
    ctx.moveTo(lastRef.current.x, lastRef.current.y);
    ctx.lineTo(p.x, p.y);
    ctx.stroke();
    lastRef.current = p;
    if (!dirtyRef.current) {
      dirtyRef.current = true;
      setTick((n) => n + 1);
    }
  };

  const end = () => { drawingRef.current = false; };

  const clear = () => {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext("2d");
    const rect = canvas.getBoundingClientRect();
    ctx.fillStyle = "#ffffff";
    ctx.fillRect(0, 0, rect.width, rect.height);
    dirtyRef.current = false;
    setTick((n) => n + 1);
  };

  useImperativeHandle(ref, () => ({
    toDataURL: () => {
      const canvas = canvasRef.current;
      if (!canvas) return null;
      // Downscale to max 800px width for compact payload (avoid 1MB ingress limits on iPad signatures)
      const MAX_W = 800;
      const dpr = window.devicePixelRatio || 1;
      const srcW = canvas.width;
      const srcH = canvas.height;
      const scale = Math.min(1, MAX_W / (srcW / dpr));
      const outW = Math.round((srcW / dpr) * scale);
      const outH = Math.round((srcH / dpr) * scale);
      const off = document.createElement("canvas");
      off.width = outW;
      off.height = outH;
      const octx = off.getContext("2d");
      octx.fillStyle = "#ffffff";
      octx.fillRect(0, 0, outW, outH);
      octx.drawImage(canvas, 0, 0, outW, outH);
      // JPEG quality 0.85 ~ ~30-80KB for a typical signature, safe under any ingress limit
      return off.toDataURL("image/jpeg", 0.85);
    },
    clear,
    isEmpty: () => !dirtyRef.current,
  }), []);

  return (
    <div className={`relative ${className}`}>
      <canvas
        ref={canvasRef}
        style={{ height, touchAction: "none" }}
        className="w-full bg-white rounded-lg border border-zinc-700 cursor-crosshair"
        onMouseDown={start}
        onMouseMove={move}
        onMouseUp={end}
        onMouseLeave={end}
        onTouchStart={start}
        onTouchMove={move}
        onTouchEnd={end}
        data-testid="signature-canvas"
      />
      <button
        type="button"
        onClick={clear}
        disabled={!dirtyRef.current}
        className="absolute top-2 right-2 text-xs text-zinc-500 hover:text-zinc-700 disabled:opacity-30"
        data-testid="signature-clear-button"
      >
        ✕ Cancella
      </button>
    </div>
  );
});

export default SignaturePad;
